import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const CookiePolicy = () => {
  useEffect(() => {
    document.title = 'Cookie Policy - Manica Solutions';
  }, []);

  return (
    <div className="bg-background min-h-screen">
      <Helmet>
        <meta name="description" content="Cookie Policy for Manica Solutions - Learn about how we use cookies and similar technologies on our website." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand/10 via-secondary/5 to-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gradient-brand mb-6">
              Cookie Policy
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Learn about how we use cookies and similar technologies to enhance your experience on our website.
            </p>
            <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-text-secondary">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} />
                <span>Last updated: {new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">What Are Cookies?</h2>
              <p className="text-text-secondary mb-4">
                Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, analyzing how you use our site, and personalizing content.
              </p>
              <p className="text-text-secondary">
                This Cookie Policy explains how Manica Solutions uses cookies and similar technologies on our website.
              </p>
            </div>

            {/* Types of Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-8">
                <div className="bg-muted p-6 rounded-brand">
                  <h3 className="text-xl font-semibold text-text-primary mb-3">Essential Cookies</h3>
                  <p className="text-text-secondary mb-3">
                    These cookies are necessary for the website to function properly. They enable basic functions like page navigation, access to secure areas, and form submissions.
                  </p>
                  <ul className="list-disc pl-6 text-text-secondary space-y-1">
                    <li>Authentication and security cookies</li>
                    <li>Session management cookies</li>
                    <li>Load balancing cookies</li>
                    <li>User interface customization cookies</li>
                  </ul>
                </div>

                <div className="bg-muted p-6 rounded-brand">
                  <h3 className="text-xl font-semibold text-text-primary mb-3">Analytics Cookies</h3>
                  <p className="text-text-secondary mb-3">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                  <ul className="list-disc pl-6 text-text-secondary space-y-1">
                    <li>Google Analytics cookies</li>
                    <li>Page view tracking</li>
                    <li>User behavior analysis</li>
                    <li>Performance monitoring</li>
                  </ul>
                </div>

                <div className="bg-muted p-6 rounded-brand">
                  <h3 className="text-xl font-semibold text-text-primary mb-3">Functional Cookies</h3>
                  <p className="text-text-secondary mb-3">
                    These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.
                  </p>
                  <ul className="list-disc pl-6 text-text-secondary space-y-1">
                    <li>Language preference cookies</li>
                    <li>Theme and layout preferences</li>
                    <li>Form data retention</li>
                    <li>User experience customization</li>
                  </ul>
                </div>

                <div className="bg-muted p-6 rounded-brand">
                  <h3 className="text-xl font-semibold text-text-primary mb-3">Marketing Cookies</h3>
                  <p className="text-text-secondary mb-3">
                    These cookies are used to track visitors across websites to display relevant and engaging advertisements.
                  </p>
                  <ul className="list-disc pl-6 text-text-secondary space-y-1">
                    <li>Social media integration cookies</li>
                    <li>Advertising network cookies</li>
                    <li>Retargeting cookies</li>
                    <li>Campaign tracking cookies</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Specific Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Specific Cookies We Use</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-3 text-left font-semibold text-text-primary">Cookie Name</th>
                      <th className="border border-border p-3 text-left font-semibold text-text-primary">Purpose</th>
                      <th className="border border-border p-3 text-left font-semibold text-text-primary">Duration</th>
                      <th className="border border-border p-3 text-left font-semibold text-text-primary">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-3 text-text-secondary">session_id</td>
                      <td className="border border-border p-3 text-text-secondary">Maintains user session</td>
                      <td className="border border-border p-3 text-text-secondary">Session</td>
                      <td className="border border-border p-3 text-text-secondary">Essential</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 text-text-secondary">_ga</td>
                      <td className="border border-border p-3 text-text-secondary">Google Analytics tracking</td>
                      <td className="border border-border p-3 text-text-secondary">2 years</td>
                      <td className="border border-border p-3 text-text-secondary">Analytics</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 text-text-secondary">_gid</td>
                      <td className="border border-border p-3 text-text-secondary">Google Analytics session tracking</td>
                      <td className="border border-border p-3 text-text-secondary">24 hours</td>
                      <td className="border border-border p-3 text-text-secondary">Analytics</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 text-text-secondary">language_pref</td>
                      <td className="border border-border p-3 text-text-secondary">Stores language preference</td>
                      <td className="border border-border p-3 text-text-secondary">1 year</td>
                      <td className="border border-border p-3 text-text-secondary">Functional</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3 text-text-secondary">theme_pref</td>
                      <td className="border border-border p-3 text-text-secondary">Stores theme preference</td>
                      <td className="border border-border p-3 text-text-secondary">1 year</td>
                      <td className="border border-border p-3 text-text-secondary">Functional</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Third-Party Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Third-Party Cookies</h2>
              <p className="text-text-secondary mb-4">
                We may use third-party services that place cookies on your device. These services include:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                <li><strong>Social Media Platforms:</strong> For social media integration and sharing</li>
                <li><strong>Payment Processors:</strong> For secure payment processing</li>
                <li><strong>Content Delivery Networks:</strong> For faster content delivery</li>
              </ul>
              <p className="text-text-secondary">
                These third-party services have their own privacy policies and cookie practices. We encourage you to review their policies.
              </p>
            </div>

            {/* Cookie Management */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Managing Your Cookie Preferences</h2>
              
              <h3 className="text-xl font-semibold text-text-primary mb-3">Browser Settings</h3>
              <p className="text-text-secondary mb-4">
                You can control and manage cookies through your browser settings:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>

              <h3 className="text-xl font-semibold text-text-primary mb-3">Cookie Consent</h3>
              <p className="text-text-secondary mb-4">
                When you first visit our website, you'll see a cookie consent banner. You can:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Accept all cookies</li>
                <li>Reject non-essential cookies</li>
                <li>Customize your preferences</li>
                <li>Change your preferences at any time</li>
              </ul>

              <h3 className="text-xl font-semibold text-text-primary mb-3">Opt-Out Options</h3>
              <p className="text-text-secondary mb-4">
                You can opt out of certain types of cookies:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li><strong>Analytics:</strong> Use browser add-ons to block analytics cookies</li>
                <li><strong>Advertising:</strong> Opt out through the Digital Advertising Alliance</li>
                <li><strong>Social Media:</strong> Adjust privacy settings on social media platforms</li>
              </ul>
            </div>

            {/* Impact of Disabling Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Impact of Disabling Cookies</h2>
              <p className="text-text-secondary mb-4">
                If you choose to disable cookies, some features of our website may not function properly:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>You may need to re-enter information repeatedly</li>
                <li>Some website features may not work as expected</li>
                <li>Personalization features may be limited</li>
                <li>Analytics and performance monitoring may be affected</li>
              </ul>
            </div>

            {/* Updates to Policy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Updates to This Cookie Policy</h2>
              <p className="text-text-secondary mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Posting the updated policy on our website</li>
                <li>Updating the "Last updated" date</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying a notice on our website</li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Contact Us</h2>
              <p className="text-text-secondary mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-muted p-6 rounded-brand">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="Mail" size={20} className="text-secondary" />
                    <span className="text-text-secondary">Email: privacy@manicasolutions.com</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Phone" size={20} className="text-secondary" />
                    <span className="text-text-secondary">Phone: +27110836344</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" size={20} className="text-secondary" />
                    <span className="text-text-secondary">Address: Northgate, Randburg, Johannesburg, South Africa</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Policies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Related Policies</h2>
              <p className="text-text-secondary mb-4">
                For more information about how we handle your data, please review our other policies:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <a href="/privacy-policy" className="block p-4 bg-muted rounded-brand hover:bg-secondary/10 transition-colors">
                  <h3 className="font-semibold text-text-primary mb-2">Privacy Policy</h3>
                  <p className="text-sm text-text-secondary">Learn about how we collect, use, and protect your personal information.</p>
                </a>
                <a href="/terms-of-service" className="block p-4 bg-muted rounded-brand hover:bg-secondary/10 transition-colors">
                  <h3 className="font-semibold text-text-primary mb-2">Terms of Service</h3>
                  <p className="text-sm text-text-secondary">Read our terms and conditions for using our services.</p>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
